/**
 * Payments Settings Page - Coordonnées bancaires + Stripe + PayPal + GoCardless + Email
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Save, CreditCard, Mail } from 'lucide-react';
import { Button, Spinner } from '@/components/ui-v2';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { StripeConnectSettings } from '@/features/payments/components/StripeConnectSettings';
import { PayPalSettings } from '@/features/payments/components/PayPalSettings';
import { GoCardlessSettings } from '@/features/payments/components/GoCardlessSettings';

const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
  border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
  transition-all duration-300`;

const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
  focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
  hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
  transition-all duration-300 outline-none text-sm`;

export const PaymentsSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    company,
    loading,
    saving,
    error,
    updateField,
    saveCompany,
  } = useCompanySettings();

  const handleSave = async () => {
    await saveCompany();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className={cardClasses}>
        <div className="text-center py-8">
          <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
            <CreditCard className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {error || t('settings.company.noCompany')}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton save */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-xl">
            <CreditCard className="w-5 h-5 text-green-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">{t('settings.payments.title')}</h2>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="secondary"
          icon={<Save className="w-4 h-4" />}
        >
          {saving ? t('common.actions.saving') : t('common.actions.save')}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Coordonnées bancaires */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <div className="p-2 bg-green-100 rounded-xl">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            {t('settings.payments.bankDetails')}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('settings.payments.bankDetailsDesc')}
          </p>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                IBAN <span className="text-gray-400 text-xs">({t('settings.payments.recommendedForFacturX')})</span>
              </label>
              <input
                type="text"
                value={company.iban || ""}
                onChange={(e) => updateField("iban", e.target.value)}
                className={inputClasses}
                placeholder="FR76 1234 5678 9012 3456 7890 123"
                maxLength={34}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                BIC/SWIFT <span className="text-gray-400 text-xs">({t('common.optional')})</span>
              </label>
              <input
                type="text"
                value={company.bic || ""}
                onChange={(e) => updateField("bic", e.target.value)}
                className={inputClasses}
                placeholder="BNPAFRPPXXX"
                maxLength={11}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.payments.defaultPaymentTerms')}
              </label>
              <textarea
                value={company.lateFeePolicy || ""}
                onChange={(e) => updateField("lateFeePolicy", e.target.value)}
                className={`${inputClasses} resize-none`}
                placeholder={t('settings.payments.paymentTermsPlaceholder')}
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.payments.availableVariables')} : {'{payment_conditions}'}, {'{iban}'}, {'{bic}'}
              </p>
            </div>
          </div>
        </div>

        {/* Email de contact */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <div className="p-2 bg-purple-100 rounded-xl">
              <Mail className="w-5 h-5 text-purple-600" />
            </div>
            {t('settings.payments.contactEmail')}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {t('settings.payments.contactEmailDesc')}
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.payments.contactEmailLabel')} <span className="text-gray-400 text-xs">({t('settings.payments.forReplies')})</span>
              </label>
              <input
                type="email"
                value={company.replyToEmail || ""}
                onChange={(e) => updateField("replyToEmail", e.target.value)}
                className={inputClasses}
                placeholder={t('settings.payments.contactEmailPlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('settings.payments.defaultEmailNote')}
              </p>
            </div>

            <div className="bg-teal-50/50 border border-teal-200/50 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-teal-100 rounded-lg">
                  <Mail className="w-4 h-4 text-teal-600" />
                </div>
                <div className="text-sm text-teal-800">
                  <p className="font-semibold mb-1">{t('settings.payments.howItWorks')}</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>{t('settings.payments.howItWorks1')}</li>
                    <li>{t('settings.payments.howItWorks2')}</li>
                    <li>{t('settings.payments.howItWorks3')}</li>
                    <li>{t('settings.payments.howItWorks4')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stripe Connect */}
        <StripeConnectSettings />

        {/* PayPal */}
        <PayPalSettings />

        {/* GoCardless - SEPA Direct Debit */}
        <GoCardlessSettings />
      </div>
    </div>
  );
};

export default PaymentsSettingsPage;
